/**
 * How often the rent falls due: a count and a period, entered separately.
 *
 * A fixed list of names cannot cover what real agreements say. "Annual", "quarterly" and
 * "every 2 years" were all there, but a three-year commercial lease, a fortnightly short let and
 * an eighteen-month term were not — and each one would have meant inventing another word for the
 * dropdown. A number beside a period says all of them and needs no new vocabulary.
 *
 * The count defaults to 1, which is what every existing record means, so nothing had to be
 * migrated and nobody has to fill this in unless their term is unusual.
 */

import { CYCLE_UNITS, cycleLabel, termLabel, toTerm, type RentCycle } from "@rentorng/core";
import { Input, Select } from "./ui";

export function TermInput({
  cycle,
  every,
  onCycle,
  onEvery,
  disabled,
  idPrefix = "rentCycle",
}: {
  cycle: string;
  every: number;
  onCycle: (next: string) => void;
  onEvery: (next: number) => void;
  disabled?: boolean;
  idPrefix?: string;
}) {
  const oneOff = cycle === "one_off";
  const term = toTerm(cycle as RentCycle, every);

  return (
    <div className="min-w-0">
      {/*
        It has to survive being half of a two-column card.
        "Every", a count and a period side by side needs about 270px; the column it sits in is
        often 250. It wraps rather than pushing the select out through the side of the card, which
        is what it did before.
      */}
      <div className="flex min-w-0 flex-wrap items-start gap-2">
        <span className="pt-2.5 text-sm text-ink-500">Every</span>
        <Input
          id={`${idPrefix}Every`}
          name={`${idPrefix}Every`}
          placeholder="1"
          inputMode="numeric"
          value={oneOff ? "" : String(every)}
          disabled={disabled || oneOff}
          onChange={(event) => onEvery(Math.max(1, Math.floor(Number(event.target.value) || 1)))}
          className="w-16 shrink-0"
          aria-label="How many"
        />
        <Select
          id={idPrefix}
          name={idPrefix}
          value={cycle}
          disabled={disabled}
          onChange={(event) => onCycle(event.target.value)}
          className="min-w-[7.5rem] flex-1"
          aria-label="Period"
        >
          {CYCLE_UNITS.map((unit) => (
            <option key={unit} value={unit}>
              {/* "week" reads better than "Weekly" after a number, and the count is almost always
                  1 — so the plural is left to the summary line below rather than the option. */}
              {cycleLabel(unit)}
            </option>
          ))}
          <option value="one_off">One-off</option>
        </Select>
      </div>

      {/* Says back what was entered, in words. A number and a dropdown can be read two ways;
          "Every 3 years" cannot. */}
      <p className="mt-1.5 text-xs text-ink-500">
        {oneOff ? "Charged once, at the start of the tenancy." : `${termLabel(term)} — rent falls due once each period.`}
      </p>
    </div>
  );
}
